import React, { ElementType, forwardRef } from 'react';
import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { CommonHTMLAttributes } from '@frontend/types';
import { WeaveThemeValues } from '../../../component-theme/theme-types';
import { createKeydownHandler } from '../../../helpers';
import { useThemeValues } from '../../../hooks';
import { IconProps } from '../../../icon';
import { NakedButton } from '../../../naked';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../../type-utils';
import { Chip } from '../../chip';
import { IconButton } from '../../icon-button';
import { Text } from '../../text';
import { useTooltip } from '../../tooltip';
import { Truncated } from '../../truncated';
import { AccordionChevronPosition } from '../accordion.types';
import { AccordionStyles } from '../common-styles';
import { useAccordionContext } from '../provider/accordion.provider';
import { useAccordionItemContext } from './accordion-item';
import { DefaultAccordionHeader } from './default-accordion-header';

type Props = {
  as?: ElementType;
  title?: string;
  subtitle?: string;
  disabled?: boolean;
  chevronIcon?: React.FC<React.PropsWithChildren<IconProps>> | boolean;
  children?: React.ReactNode;
};

type AccordionHeaderProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<C, Props>;

const locationPadding = {
  small: [1, 2],
  large: [1.5, 2],
};

type AccordionHeaderComponent = <C extends React.ElementType = 'section'>(
  props: AccordionHeaderProps<C>
) => React.ReactNode;

// eslint-disable-next-line react/display-name
export const AccordionHeaderOptional: AccordionHeaderComponent = forwardRef(
  <C extends React.ElementType = 'section'>(
    { disabled, onKeyDown, onClick, chevronIcon, children, title, subtitle, as, ...others }: AccordionHeaderProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const {
      isItemActive,
      headingLevel,
      onChange,
      getHeaderId,
      getBodyId,
      shouldLoop,
      isInteractive,
      variant,
      chevronPosition,
      chevronIcon: globalChevron,
      chevronSize,
      padding: headerPadding,
      hasMultiLocations: hasMultiLocationsGlobal,
      size,
      showBorderBottomAlways,
      startAdornment,
    } = useAccordionContext();
    const { value, hasMultiLocations, trackingId } = useAccordionItemContext();
    const isActive = isItemActive(value);
    const { spacing } = useThemeValues();
    const accordionHeaderStyles = AccordionStyles.accordionHeader({
      chevronPosition,
      isActive,
      variant,
      isInteractive,
      isDisabled: disabled,
      hasMultiLocations: hasMultiLocations ?? hasMultiLocationsGlobal,
      showBorderBottomAlways,
    });
    const chevronContainerStyle = AccordionStyles.chevronContainer({ disabled });

    const conditionalProps = {
      disabled: disabled,
      onClick: (event) => {
        onClick?.(event);
        onChange?.(value);
      },
      'data-active': isActive || undefined,
      'aria-expanded': isActive,
      'aria-controls': getBodyId(value),
      id: getHeaderId(value),
      'data-trackingid': trackingId,
    };

    const handleConditionalChevronRender = () => {
      const ChevronIcon = chevronIcon || globalChevron;

      if (!ChevronIcon || typeof ChevronIcon === 'boolean') return null;

      if (isInteractive) {
        return (
          <IconButton label='chevron' css={chevronContainerMarginStyle(spacing, chevronPosition)} {...conditionalProps}>
            <div css={chevronAnimatedStyle(isActive)}>
              <ChevronIcon size={chevronSize ?? variant === 'location' ? 16 : 24} />
            </div>
          </IconButton>
        );
      }

      return (
        <section css={[chevronContainerStyle, chevronContainerMarginStyle(spacing, chevronPosition)]}>
          <div css={chevronAnimatedStyle(isActive)}>
            <ChevronIcon size={chevronSize ?? variant === 'location' ? 16 : 24} />
          </div>
        </section>
      );
    };

    const MainHeading = headingLevel ? `h${headingLevel}` : ('h3' as React.ElementType);
    const Component = (as as 'article') ?? isInteractive ? 'article' : NakedButton;

    return (
      <MainHeading
        css={css`
          width: 100%;
          height: 100%;
          all: unset;
          display: flex;
        `}
      >
        <Component
          {...others}
          {...(!isInteractive && conditionalProps)}
          ref={ref}
          data-accordion-control
          onKeyDown={createKeydownHandler({
            siblingSelector: '[data-accordion-control]',
            parentSelector: '[data-accordion]',
            shouldActivateOnFocus: false,
            shouldLoop,
            orientation: 'vertical',
            onKeyDown,
          })}
          css={accordionHeaderStyles}
        >
          {handleConditionalChevronRender()}
          <div
            className='header-container'
            css={css`
              flex: 1;
              display: flex;
              width: 100%;
              padding: ${typeof headerPadding !== 'undefined'
                ? spacing(...(typeof headerPadding === 'number' ? [headerPadding] : headerPadding))
                : spacing(...(variant === 'location' && size ? locationPadding[size] : [2]))};
              overflow: hidden;
            `}
          >
            {startAdornment}
            <DefaultAccordionHeader title={title} subtitle={subtitle} />
            {children}
          </div>
        </Component>
      </MainHeading>
    );
  }
);

const chevronAnimatedStyle = (isActive: boolean) => css`
  transition: transform 300ms ease-out;
  display: flex;
  flex-direction: center;
  justify-content: center;
  transform: rotate(${isActive ? 180 : 0}deg);
`;

const chevronContainerMarginStyle = (
  spacing: WeaveThemeValues['Spacing'],
  chevronPosition?: AccordionChevronPosition
) => {
  if (chevronPosition === 'left') {
    return css`
      margin-left: ${spacing(1)};
    `;
  }
  return css`
    margin-right: ${spacing(1)};
  `;
};

type AccordionIconHeaderProps = {
  title: string;
  icon: IconName;
  infoTooltip?: string;
  timezone?: string;
} & CommonHTMLAttributes;

const AccordionIconHeader = ({ title, icon, infoTooltip, timezone, ...rest }: AccordionIconHeaderProps) => {
  const { spacing } = useThemeValues();
  const { isItemActive } = useAccordionContext();
  const { value } = useAccordionItemContext();
  const isActive = isItemActive(value);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  return (
    <span
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing(1)};
        overflow: hidden;
      `}
      {...rest}
    >
      <Icon name={icon} size={16} css={{ flexShrink: 0 }} />
      <Truncated as={Text} size='large' css={{ margin: 0, color: 'inherit' }}>
        {title}
      </Truncated>

      {!!infoTooltip && (
        <span {...triggerProps} css={{ flexShrink: 0 }}>
          <Icon name='info-small' size={16} css={{ color: 'inherit' }} />
        </span>
      )}
      {!!timezone && <Chip.TimeZone variant={isActive ? 'neutralDark' : 'neutral'}>{timezone}</Chip.TimeZone>}
      <Tooltip {...tooltipProps}>{infoTooltip}</Tooltip>
    </span>
  );
};

const AccordionLocationHeader = (props: Omit<AccordionIconHeaderProps, 'icon'>) => {
  const { hasMultiLocations: hasMultiLocationsGlobal } = useAccordionContext();
  const { hasMultiLocations } = useAccordionItemContext();

  const icon: IconName = hasMultiLocations ?? hasMultiLocationsGlobal ? 'locations-small' : 'location-small';

  return <AccordionIconHeader {...props} icon={icon} />;
};

const AccordionHeaderNamespace = Object.assign(AccordionHeaderOptional, {
  Location: AccordionLocationHeader,
  Icon: AccordionIconHeader,
});

export { AccordionHeaderNamespace as AccordionHeader };
